import React, { useState, useEffect } from "react";
import adminAxios from "../admin/adminAxios";
import AdminSidebar from "../admin/AdminSidebar";
import { 
  Monitor, 
  Trash2, 
  UploadCloud, 
  ShieldCheck, 
  Image as ImageIcon, 
  Video, 
  RefreshCcw 
} from "lucide-react";

/**
 * PREMIUM CYBER IDENTITY CONFIGURATION
 */
const COLORS = {
  cyan: "#03f8d7",
  rose: "#ff2d75",
  slate: "#0f172a",
  surface: "rgba(15, 23, 42, 0.95)",
  border: "rgba(3, 248, 215, 0.15)",
  textMain: "#f8fafc",
  textMuted: "#94a3b8",
  success: "#10b981"
};

const SERVICE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

function AdminBanners() {
  const [banners, setBanners] = useState([
    { banner_index: 1, path: "" },
    { banner_index: 2, path: "" },
    { banner_index: 3, path: "" },
    { banner_index: 4, path: "" },
    { banner_index: 5, path: "" },
    { banner_index: 6, path: "" }
  ]);
  const [loadingSlot, setLoadingSlot] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");

  // Extract tenant identifier
  const adminSession = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const tenantId = adminSession.adminId || localStorage.getItem("adminId");

  /* ================= SYNCHRONIZATION ================= */
  useEffect(() => {
    syncAssets();
  }, []);

  const syncAssets = async () => {
    try {
      const res = await adminAxios.get("/api/admin/banners", {
        headers: tenantId ? { "x-tenant-id": tenantId } : {}
      });
      const mappedAssets = [1, 2, 3, 4, 5, 6].map(id => {
        const match = res.data.find(item => item.banner_index === id);
        return match ? match : { banner_index: id, path: "" };
      });
      setBanners(mappedAssets);
    } catch (err) {
      console.error("[Node Sync] Interrupt:", err);
    }
  };

  /* ================= OPERATIONS ================= */
  const handleFileUpload = async (slotId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const bundle = new FormData();
    bundle.append("bannerFile", file);
    if (tenantId) bundle.append("adminId", tenantId);

    setLoadingSlot(slotId);
    setStatusMsg("");

    try {
      const res = await adminAxios.post(`/api/admin/banners/upload/${slotId}`, bundle, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...(tenantId ? { "x-tenant-id": tenantId } : {})
        }
      });
      
      if (res.data && res.data.banner) {
        setBanners(prev => prev.map(item => item.banner_index === slotId ? res.data.banner : item));
        setStatusMsg(`Slot ${slotId} synchronization successful.`);
      } else {
        syncAssets(); 
      }
      setTimeout(() => setStatusMsg(""), 3000);
    } catch (err) {
      setStatusMsg("Compatibility error detected.");
    } finally {
      setLoadingSlot(null);
      e.target.value = null; 
    }
  };

  const handleRemove = async (slotId) => {
    setLoadingSlot(slotId);
    try {
      await adminAxios.delete(`/api/admin/banners/${slotId}`, {
        headers: tenantId ? { "x-tenant-id": tenantId } : {}
      });
      setBanners(prev => prev.map(item => item.banner_index === slotId ? { ...item, path: "" } : item));
      setStatusMsg("Asset cleared.");
      setTimeout(() => setStatusMsg(""), 3000);
    } catch (err) {
      try {
          await adminAxios.post(`/api/admin/banners/clear/${slotId}`, {}, {
            headers: tenantId ? { "x-tenant-id": tenantId } : {}
          });
          setBanners(prev => prev.map(item => item.banner_index === slotId ? { ...item, path: "" } : item));
          setStatusMsg("Asset cleared.");
      } catch (postErr) {
          setStatusMsg("Operation failure.");
      }
    } finally {
        setLoadingSlot(null);
    }
  };

  /* ================= PATH RESOLUTION ================= */
  const getAssetSource = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith("http")) return filePath;
    const cleanPath = filePath.replace(/^\//, "").replace(/\\/g, '/');
    if (cleanPath.startsWith("uploads/")) {
      return `${SERVICE_URL}/${cleanPath}`;
    }
    return `${SERVICE_URL}/uploads/${cleanPath}`;
  };

  return (
    <div style={styles.container} data-tenant-scope={tenantId || "global"}>
      <AdminSidebar />

      <div style={styles.mainContent} className="view-panel">
        {/* TOP TELEMETRY BAR */}
        <div style={styles.topBar}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Monitor color={COLORS.cyan} size={20} />
              <h1 style={styles.topBarTitle}>PLATFORM ASSET REGISTRY</h1>
            </div>
            <p style={styles.topBarSub}>PLEASE DO NOT UPLOAD BANNERS WHICH INCLUDES CRYPTO IMAGES, IT CAN CRASH OR BAN THE WEBSITE PERMANENTLY {tenantId ? `` : ""}</p>
          </div>
          <div style={styles.liveStatus}>
             <span style={styles.statusDot}></span>
             <span style={styles.statusText}>TERMINAL ACTIVE</span>
          </div>
        </div>

        <div style={styles.contentPadding}>
          {statusMsg && (
            <div style={styles.floatingAlert}>
               <ShieldCheck size={14} color={COLORS.cyan} />
               {statusMsg}
            </div>
          )}

          <div style={styles.grid}>
            {banners.map((item) => (
              <div key={`slot-${item.banner_index}`} style={styles.tile}>
                <div style={styles.tileHeader}>
                  <div>
                    <span style={styles.nodeLabel}>ASSET NODE 0{item.banner_index}</span>
                    <div style={styles.indicatorGroup}>
                      <span style={{ 
                        ...styles.miniDot, 
                        backgroundColor: item.path ? COLORS.success : COLORS.textMuted 
                      }}></span>
                      <span style={{...styles.statusLabel, color: item.path ? COLORS.success : COLORS.textMuted}}>
                        {item.path ? "LIVE / ACTIVE" : "VACANT"}
                      </span>
                    </div>
                  </div>
                  
                  {item.path && (
                    <button 
                        onClick={() => handleRemove(item.banner_index)} 
                        style={styles.actionDelete}
                        disabled={loadingSlot === item.banner_index}
                    >
                        {loadingSlot === item.banner_index ? <RefreshCcw size={12} className="spin" /> : <Trash2 size={12} />}
                    </button>
                  )}
                </div>

                <div style={styles.previewBox}>
                  {item.path ? (
                    item.path.match(/\.(mp4|webm|ogg)$/i) ? (
                      <div style={{position: 'relative', width: '100%', height: '100%'}}>
                         <video 
                            src={getAssetSource(item.path)} 
                            autoPlay muted loop playsInline
                            style={styles.previewMedia} 
                          />
                          <div style={styles.mediaTag}><Video size={10} /> VIDEO</div>
                      </div>
                    ) : (
                      <div style={{position: 'relative', width: '100%', height: '100%'}}>
                        <img 
                          src={getAssetSource(item.path)} 
                          alt="Media Preview" 
                          style={styles.previewMedia} 
                          onError={(e) => { e.target.src = "https://placehold.co/400x180?text=Sync+Failed"; }}
                        />
                        <div style={styles.mediaTag}><ImageIcon size={10} /> IMAGE</div>
                      </div>
                    )
                  ) : (
                    <div style={styles.emptyState}>
                      <UploadCloud size={32} color={COLORS.textMuted} style={{ marginBottom: "12px", opacity: 0.5 }} />
                      <span style={{letterSpacing: '1px'}}>NODE READY FOR DEPLOYMENT</span>
                    </div>
                  )}
                </div>

                <div style={styles.footer}>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => handleFileUpload(item.banner_index, e)}
                    style={{ display: "none" }}
                    id={`asset-input-${item.banner_index}`}
                  />
                  <label htmlFor={`asset-input-${item.banner_index}`} style={{
                    ...styles.actionUpload,
                    background: loadingSlot === item.banner_index ? "rgba(255,255,255,0.05)" : COLORS.cyan,
                    color: loadingSlot === item.banner_index ? COLORS.textMuted : COLORS.slate,
                    cursor: loadingSlot === item.banner_index ? "not-allowed" : "pointer",
                    border: `1px solid ${COLORS.cyan}`
                  }}>
                    {loadingSlot === item.banner_index ? "SYNCHRONIZING..." : "REPLACE ASSET"}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes activePulse {
          0% { transform: scale(0.9); opacity: 1; }
          70% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(0.9); opacity: 0; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }
        .view-panel { transition: margin-left 0.3s ease; }
        @media (max-width: 992px) {
          .view-panel { margin-left: 0 !important; }
        }
      `}</style>
    </div>
  );
}

/* ================= COMPONENT STYLES ================= */

const styles = {
  container: { display: "flex", minHeight: "100vh", backgroundColor: "#060608", fontFamily: "'Inter', sans-serif", color: COLORS.textMain },
  mainContent: { flexGrow: 1, marginLeft: "280px", position: "relative", display: "flex", flexDirection: "column" },
  topBar: { background: COLORS.surface, backdropFilter: "blur(10px)", padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${COLORS.border}`, position: 'sticky', top: 0, zIndex: 10 },
  topBarTitle: { margin: 0, fontSize: "18px", fontWeight: "900", letterSpacing: "2px" },
  topBarSub: { margin: "4px 0 0 0", fontSize: "9px", color: COLORS.cyan, fontWeight: "800", letterSpacing: "1px" },
  liveStatus: { display: "flex", alignItems: "center", gap: "10px", background: "rgba(0,0,0,0.3)", padding: "8px 15px", borderRadius: "8px", border: `1px solid ${COLORS.border}` },
  statusDot: { height: 8, width: 8, backgroundColor: COLORS.cyan, borderRadius: "50%", animation: "activePulse 2s infinite" },
  statusText: { fontSize: "9px", fontWeight: "900", color: COLORS.cyan, letterSpacing: "1px" },
  contentPadding: { padding: "40px" },
  floatingAlert: { background: COLORS.slate, color: COLORS.cyan, padding: "14px 25px", borderRadius: "10px", position: "fixed", bottom: "30px", right: "30px", zIndex: 1000, fontSize: "11px", fontWeight: "900", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", border: `1px solid ${COLORS.cyan}`, display: 'flex', alignItems: 'center', gap: '10px', letterSpacing: '1px' },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "30px" },
  tile: { background: COLORS.surface, borderRadius: "20px", padding: "25px", border: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", gap: "20px", boxShadow: "0 10px 20px rgba(0,0,0,0.2)" },
  tileHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  nodeLabel: { fontWeight: "900", fontSize: "10px", color: COLORS.textMuted, letterSpacing: "2px" },
  indicatorGroup: { display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" },
  miniDot: { width: "6px", height: "6px", borderRadius: "50%" },
  statusLabel: { fontSize: "10px", fontWeight: "800", letterSpacing: "0.5px" },
  actionDelete: { background: "rgba(255, 45, 117, 0.1)", color: COLORS.rose, border: `1px solid rgba(255, 45, 117, 0.2)`, width: "32px", height: "32px", borderRadius: "8px", cursor: "pointer", display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.2s' },
  previewBox: { width: "100%", height: "200px", background: "rgba(0,0,0,0.2)", borderRadius: "15px", overflow: "hidden", border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center" },
  previewMedia: { width: "100%", height: "100%", objectFit: "cover" },
  mediaTag: { position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.6)', padding: '5px 10px', borderRadius: '5px', fontSize: '9px', fontWeight: '900', color: COLORS.cyan, display: 'flex', alignItems: 'center', gap: '5px', backdropFilter: 'blur(4px)' },
  emptyState: { textAlign: "center", color: COLORS.textMuted, fontSize: "10px", fontWeight: "800" },
  footer: { marginTop: "5px" },
  actionUpload: { display: "block", width: "100%", textAlign: "center", padding: "14px", borderRadius: "10px", fontWeight: "900", fontSize: "11px", letterSpacing: "1px", transition: '0.2s' }
};

export default AdminBanners;